import { Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select } from "antd";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import dayjs from "dayjs";
import PageTitle from "../page-header/PageHeader";
import { updateWorker } from "../../redux/actions/hr/workerActions";
import { getAllMachines } from "../../api/machineApi";

const UpdateWorker = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const worker = state?.data;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [salaryType, setSalaryType] = useState(worker?.salary_type || "monthly");
  const [machines, setMachines] = useState([]);

  const watchedMonthlySalary = Form.useWatch("monthly_salary", form);
  const watchedHourlyRate = Form.useWatch("hourly_rate", form);
  const salaryChanged =
    salaryType !== worker?.salary_type ||
    (salaryType === "monthly" && watchedMonthlySalary !== worker?.monthly_salary) ||
    (salaryType === "hourly" && watchedHourlyRate !== worker?.hourly_rate);

  useEffect(() => {
    getAllMachines({ query: "all", is_inhouse: true }).then((res) => {
      const list = Array.isArray(res) ? res : res?.getAllMachine || [];
      setMachines(list.filter((m) => m.is_active));
    }).catch(() => {});
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    const payload = {
      ...values,
      join_date: values.join_date ? values.join_date.toISOString() : null,
      leave_date: values.leave_date ? values.leave_date.toISOString() : null,
      salary_effective_from: salaryChanged && values.salary_effective_from ? values.salary_effective_from.toISOString() : undefined,
      salary_change_note: salaryChanged ? values.salary_change_note : undefined,
    };
    const resp = await dispatch(updateWorker(id, payload));
    setLoading(false);
    if (resp.message === "success") navigate(`/hr/workers/${id}`);
  };

  if (!worker) return <div>No worker data. Go back and try again.</div>;

  return (
    <>
      <PageTitle title="Back" />
      <Card title={`Update Worker: ${worker.name}`}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            ...worker,
            join_date: worker.join_date ? dayjs(worker.join_date) : null,
            leave_date: worker.leave_date ? dayjs(worker.leave_date) : null,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="phone" label="Phone"><Input /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="id_no" label="ID No"><Input /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="department" label="Department"><Input /></Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="designation" label="Designation"><Input /></Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="worker_type" label="Worker Type">
                <Select allowClear placeholder="Select type">
                  <Select.Option value="skilled">Skilled</Select.Option>
                  <Select.Option value="unskilled">Unskilled</Select.Option>
                  <Select.Option value="supervisor">Supervisor</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="join_date" label="Join Date"><DatePicker style={{ width: "100%" }} /></Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="leave_date" label="Leave Date"><DatePicker style={{ width: "100%" }} /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="salary_type" label="Salary Type" rules={[{ required: true }]}>
                <Select onChange={(v) => { setSalaryType(v); if (v === "hourly") form.setFieldValue("working_days", undefined); }}>
                  <Select.Option value="monthly">Monthly</Select.Option>
                  <Select.Option value="hourly">Hourly</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            {salaryType === "monthly" && (
              <Col xs={24} md={6}>
                <Form.Item name="monthly_salary" label="Monthly Salary (₹)" rules={[{ required: true }]}>
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
            )}
            {salaryType === "hourly" && (
              <Col xs={24} md={6}>
                <Form.Item name="hourly_rate" label="Hourly Rate (₹)" rules={[{ required: true }]}>
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
            )}
            {salaryType === "monthly" && (
              <Col xs={24} md={6}>
                <Form.Item name="shift_hours" label="Shift Hours/Day">
                  <InputNumber style={{ width: "100%" }} min={1} max={24} />
                </Form.Item>
              </Col>
            )}
            {salaryType === "monthly" && (
              <Col xs={24} md={6}>
                <Form.Item name="working_days" label="Working Days">
                  <Select allowClear placeholder="Select">
                    <Select.Option value="mon-sat">Mon – Sat (6 days)</Select.Option>
                    <Select.Option value="mon-fri">Mon – Fri (5 days)</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={6}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Select.Option value={true}>Active</Select.Option>
                  <Select.Option value={false}>Inactive</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {salaryChanged && (
            <Row gutter={16}>
              <Col xs={24}>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Salary changed — this will be recorded as a salary revision. Set the date it takes effect."
                />
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="salary_effective_from"
                  label="Salary Effective From"
                  rules={[{ required: true, message: "Effective date is required to change salary" }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}>
                <Form.Item name="salary_change_note" label="Reason for revision">
                  <Input placeholder="Reason for salary change" />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="machine_ids" label="Machines Can Operate">
                <Select mode="multiple" allowClear placeholder="Select machines" optionFilterProp="children" showSearch>
                  {machines.map((m) => (
                    <Select.Option key={m.id} value={m.id}>{m.name} ({m.code})</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Address"><Input.TextArea rows={2} /></Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>Update Worker</Button>
        </Form>
      </Card>
    </>
  );
};

export default UpdateWorker;
